import useBundleTranslation from 'i18n';
import React, { useEffect, useRef, useState } from 'react';
import { alpha, Box, Button, DialogActions, Input, Skeleton, Stack, Theme } from '@mui/material';
import IconMi from 'components/common/icon/IconMi';
import { Popup } from 'components/common/popup/Popup';
import ReportAlertRule from '../report-alert-rule/ReportAlertRule';
import useAlertRuleData from '../report-alert-rule/useAlertRuleData';
import { StaticAddon } from 'components/common/static-addon/StaticAddon';
import Alert from '@mui/material/Alert';
import useRuleDisplayTemplate from '../report-alert-rule/useRuleDisplayTemplate';
import { reportAlertRuleAPI } from 'api/viewer/report-alert-rule';
import LoadingPlaceholder from 'components/common/loading-placeholder/LoadingPlaceholder';
import toast from 'app/toast';
import { useQueryClient } from '@tanstack/react-query';
import useCustomSimplifiedForm from 'components/common/form/hooks/useCustomSimplifiedForm';
import Moment from 'moment';
import GenerateAlertEventsForm from './GenerateAlertEventsForm';

const wizardSteps = ['type', 'criteria', 'finish'];

function WizardDialogActions({
    wizardStep,
    onClose,
    onChangeStep,
}: {
    wizardStep: number;
    onClose: () => void;
    onChangeStep: (step: number) => void;
}) {
    const { t } = useBundleTranslation(['components/report-content', 'app/report/report-alert-rule']);
    const isLastStep = wizardSteps.length == wizardStep + 1;
    return (
        <DialogActions sx={{ justifyContent: 'space-between', py: 2, px: 3 }}>
            <Box>
                {wizardStep == 0 ? (
                    <Button variant={'outlined'} onClick={onClose} color={'neutral'}>
                        {t('cancel')}
                    </Button>
                ) : (
                    <Button
                        variant={'outlined'}
                        onClick={() => onChangeStep(wizardStep - 1)}
                        color={'neutral'}
                        startIcon={<IconMi icon="chevron-left" />}
                    >
                        {t('back')}
                    </Button>
                )}
            </Box>
            <Box>
                <Button
                    data-test={isLastStep ? 'alert-rule-finish_button' : 'alert-rule-next-step_button'}
                    onClick={() => onChangeStep(wizardStep + 1)}
                    color={isLastStep ? 'success' : undefined}
                >
                    {t(isLastStep ? 'save' : 'next')}
                </Button>
            </Box>
        </DialogActions>
    );
}

function AlertRuleNameInput({
    ruleName,
    setRuleName,
    ruleNameInputRef,
}: {
    ruleName: string;
    setRuleName: (name: string) => void;
    ruleNameInputRef: React.RefObject<HTMLInputElement>;
}) {
    const { t } = useBundleTranslation(['components/report-content', 'app/report/report-alert-rule']);
    return (
        <Box flexGrow="1" sx={{ display: 'flex', flexWrap: 'nowrap', pb: 2 }}>
            <StaticAddon>{t('alert_rule_name')}</StaticAddon>
            <Input
                fullWidth
                autoFocus
                inputRef={ruleNameInputRef}
                name={'alert_rule_name'}
                type={'text'}
                value={ruleName}
                onChange={(e) => setRuleName(e.target.value)}
                sx={{
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0,
                }}
            />
        </Box>
    );
}

export default function ReportAlertRuleWizard({
    isOpen,
    setIsOpen,
    elementId,
    alertRuleId,
}: {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    elementId: number;
    alertRuleId: number;
}) {
    const { t } = useBundleTranslation(['components/report-content', 'app/report/report-alert-rule']);
    const queryClient = useQueryClient();
    const { ruleData, setRuleData, loadRuleData } = useAlertRuleData(elementId, alertRuleId);
    const displayTemplate = useRuleDisplayTemplate();
    const [wizardStep, setWizardStep] = useState(0);
    const [errorMessage, setErrorMessage] = useState('');
    const [ruleName, setRuleName] = useState('');
    const generateAlertEventsFormData = useCustomSimplifiedForm({
        generate_alert_events: 'Y',
        date_from: null,
        date_to: null,
    });

    const ruleNameInputRef = useRef<HTMLInputElement>(null);
    const focusRuleNameInput = () => ruleNameInputRef.current?.focus();
    const handleClose = () => {
        setIsOpen(false);
        setWizardStep(0);
        setErrorMessage('');
        setRuleName('');
    };

    useEffect(() => {
        if (ruleData) {
            setRuleName(ruleData.alertRuleData.name);
        }
    }, [ruleData]);

    useEffect(() => {
        if (!ruleData) {
            return;
        }
        if (ruleData.datasetInstances.length <= 1) {
            return;
        }
        const maxUnix = Math.max(...ruleData.datasetInstances.map((i) => Moment(i.measurement_time_raw).unix()));
        const maxDateStr = Moment.unix(maxUnix).format('YYYY-MM-DD');
        generateAlertEventsFormData.form.hookFormSetValue('date_from', maxDateStr);
        generateAlertEventsFormData.form.hookFormSetValue('date_to', maxDateStr);
    }, [ruleData]);

    const handleChangeStep = async (newStep: number) => {
        if (!ruleData) {
            return;
        }
        setErrorMessage('');
        if (wizardStep == 0) {
            if (ruleName.trim() == '') {
                setErrorMessage(t('wizard.validation.alert_rule_name_required'));
                focusRuleNameInput();
                return;
            }

            if (ruleData.alertRuleData.name != ruleName) {
                setRuleData((prevState) => {
                    if (!prevState) {
                        return prevState;
                    }
                    prevState.alertRuleData.name = ruleName;
                    return prevState;
                });
            }
        } else if (wizardStep == 1) {
            if (!displayTemplate.ruleDisplayTemplate.trim()) {
                setErrorMessage(t('wizard.validation.alert_rule_template_required'));
                return false;
            }
        } else if (newStep == wizardSteps.length) {
            // Save Alert Rule
            const savedAlertRuleId = await saveRule();
            if (savedAlertRuleId > 0) {
                const testData = generateAlertEventsFormData.form.hookFormGetValues();
                if (testData['generate_alert_events'] == 'Y') {
                    setShowLoader(true);
                    await reportAlertRuleAPI.generateAlertEvents(
                        elementId,
                        savedAlertRuleId,
                        testData['date_from'],
                        testData['date_to'],
                    );
                    setShowLoader(false);
                }
                toast('success', t('success'), t('wizard.alert_saved'));
                queryClient.invalidateQueries({ queryKey: ['reportAlertRuleGrid'] });
                handleClose();
            }
            return;
        }
        setWizardStep(newStep);
    };
    const [showLoader, setShowLoader] = useState(false);
    const [alertJson, setAlertJson] = useState('{}');
    const saveRule: () => Promise<number> = async () => {
        setShowLoader(true);
        return await reportAlertRuleAPI
            .saveAlertRule(elementId, alertRuleId, alertJson, displayTemplate.ruleDisplayTemplate, ruleName)
            .then((response) => {
                if (response.status == 'OK') {
                    //@ts-ignore
                    return response.id;
                }
                if (response.status == 'ERROR') {
                    if (response.message) {
                        setErrorMessage(response.message);
                    }
                }
                return 0;
            })
            .finally(() => {
                setShowLoader(false);
            });
    };

    if (alertRuleId == 0) {
        if (!ruleData) {
            return null;
        }
        // Alert evaluation reads the whole Dataset and ignores the Dataset View's conditions.
        if (ruleData.reportData.dataset_filter_id > 0) {
            return (
                <Popup
                    maxWidth={'popupSm'}
                    onHide={handleClose}
                    open={isOpen}
                    settings={{ uid: 'dataset_view_sourced_report', noCancel: true, textOK: t('ok') }}
                >
                    {t('wizard.dataset_view_sourced_report')}
                </Popup>
            );
        }
    }

    return (
        <Popup
            maxWidth={'popupXl'}
            onHide={handleClose}
            open={isOpen}
            settings={{
                title: t('alert_rules'),
                noButtons: true,
                customActionPanel: (
                    <WizardDialogActions
                        wizardStep={wizardStep}
                        onClose={handleClose}
                        onChangeStep={handleChangeStep}
                    />
                ),
            }}
            onConfirm={() => handleChangeStep(wizardStep + 1)}
        >
            <Stack
                sx={{
                    mx: -3,
                    mt: -2,
                    backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.04),
                    py: 1,
                    px: 3,
                    mb: 2,
                }}
                direction={'row'}
                spacing={1}
            >
                {wizardSteps.map((step, index) => {
                    return (
                        <Box
                            key={step}
                            onClick={() => handleChangeStep(index)}
                            className={`step-item ${wizardStep == index ? 'step-item--active' : ''}`}
                            sx={{
                                alignItems: 'center',
                                display: 'flex',
                                '&:not(.step-item--active):hover': {
                                    cursor: 'pointer',
                                },
                            }}
                        >
                            <Box
                                sx={{
                                    width: '24px',
                                    height: '24px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    border: (theme) => `1px solid ${theme.palette.primary.main}`,
                                    color: 'primary.main',
                                    borderRadius: 1,
                                    mr: 1,
                                    '.step-item--active &, .step-item:not(.step-item--active):hover &': {
                                        backgroundColor: 'primary.main',
                                        color: 'background.default',
                                        fontWeight: '600',
                                    },
                                }}
                                className={wizardStep == index ? 'active' : undefined}
                            >
                                {index + 1}
                            </Box>
                            <Box
                                sx={{
                                    color: 'primary.main',
                                    '.step-item:not(.step-item--active):hover &': {
                                        opacity: 0.75,
                                    },
                                }}
                            >
                                {t(step)}
                            </Box>
                            {wizardSteps.length != index + 1 && (
                                <IconMi
                                    icon={'chevron-right'}
                                    sx={{
                                        ml: 0.5,
                                        fontSize: '16px',
                                        color: (theme: Theme) => alpha(theme.palette.text.primary, 0.24),
                                    }}
                                />
                            )}
                        </Box>
                    );
                })}
            </Stack>
            {errorMessage.length > 0 && (
                <Box sx={{ mb: 2 }}>
                    <Alert severity={'error'} variant={'standard'}>
                        {errorMessage}
                    </Alert>
                </Box>
            )}
            {showLoader && <LoadingPlaceholder relativeToParent />}
            {ruleData ? (
                <>
                    <Box className={wizardStep < 2 ? '' : 'd-none'}>
                        <ReportAlertRule
                            elementId={elementId}
                            reportAlertRuleId={alertRuleId}
                            displayMode={'wizard'}
                            wizardStep={wizardStep}
                            ruleData={ruleData}
                            displayTemplate={displayTemplate}
                            loadRuleData={loadRuleData}
                            nameComponent={
                                <AlertRuleNameInput
                                    ruleName={ruleName}
                                    setRuleName={setRuleName}
                                    ruleNameInputRef={ruleNameInputRef}
                                />
                            }
                            onJsonChange={(json) => setAlertJson(json)}
                        />
                    </Box>
                    <Box className={wizardStep == 2 ? '' : 'd-none'}>
                        <GenerateAlertEventsForm
                            elementProps={generateAlertEventsFormData}
                            datasetInstances={ruleData.datasetInstances}
                            withHistory={ruleData.datasetData.save_historical_instances_ind == 'Y'}
                        />
                    </Box>
                </>
            ) : (
                <Box>
                    <Skeleton variant="rectangular" width={'100%'} height={160} />
                </Box>
            )}
        </Popup>
    );
}
